import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Filter } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { wholesaleService } from '../../services/wholesaleService';

const WholesaleCatalog = () => {
  const [products, setProducts] = useState([]);
  const { user } = useAuth();
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    varietal: '',
    vintage: '',
    priceRange: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await wholesaleService.getProducts();
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
        if (error.response?.status === 403) {
          // Handle authentication error
          localStorage.removeItem('token'); // Clear invalid token
          navigate('/login', { 
            state: { 
              message: 'Your session has expired. Please login again.' 
            }
          });
        } else {
          setError('Failed to load products. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [user, navigate]);

  const handleQuantityChange = (productId, quantity) => {
    if (quantity < 0) return;
    
    setCart(prev => ({
      ...prev,
      [productId]: quantity
    }));
  };

  const getCartTotal = () => {
    return Object.entries(cart).reduce((total, [productId, quantity]) => {
      const product = products.find(p => p.productID === parseInt(productId));
      return total + (product?.wholesalePrice || 0) * quantity;
    }, 0);
  };

const handleCheckout = async () => {
  if (Object.values(cart).some(quantity => quantity > 0)) {
    try {
      setError('');
      console.log('Starting checkout with cart:', cart);
      
      // Only include items with quantity > 0
      const nonEmptyCart = Object.entries(cart).reduce((acc, [productId, quantity]) => {
        if (quantity > 0) {
          acc[productId] = quantity;
        }
        return acc;
      }, {});
  
      console.log('Filtered cart for checkout:', nonEmptyCart);
      
      if (Object.keys(nonEmptyCart).length === 0) {
        setError('Please add items to your cart');
        return;
      }

      // Calculate order
      const calculation = await wholesaleService.calculateOrder(nonEmptyCart);
      console.log('Order calculation result:', calculation);
  
      if (calculation.success) {
        navigate('/wholesale/checkout', { 
          state: { 
            cart: nonEmptyCart,
            calculation
          }
        });
      } else {
        setError(calculation.message || 'Unable to process order. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message || 'Unable to proceed to checkout. Please try again.');
    }
  } else {
    setError('Please add items to your cart');
  }
};

  const filterProducts = (product) => {
    if (filters.varietal && product.proVarietal !== filters.varietal) return false;
    if (filters.vintage && product.proVintage !== parseInt(filters.vintage)) return false;
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      if (product.wholesalePrice < min || product.wholesalePrice > max) return false;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Wholesale Products</h2>
        <button
          className="btn btn-primary"
          onClick={handleCheckout}
          disabled={!Object.values(cart).some(quantity => quantity > 0)}
        >
          <ShoppingCart className="me-2" size={20} />
          Cart Total: ${getCartTotal().toFixed(2)}
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-auto">
              <Filter size={20} className="text-muted" />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={filters.varietal}
                onChange={(e) => setFilters(prev => ({ ...prev, varietal: e.target.value }))}
              >
                <option value="">All Varietals</option>
                {[...new Set(products.map(p => p.proVarietal))].map(varietal => (
                  <option key={varietal} value={varietal}>{varietal}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={filters.vintage}
                onChange={(e) => setFilters(prev => ({ ...prev, vintage: e.target.value }))}
              >
                <option value="">All Vintages</option>
                {[...new Set(products.map(p => p.proVintage))].map(vintage => (
                  <option key={vintage} value={vintage}>{vintage}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={filters.priceRange}
                onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
              >
                <option value="">All Prices</option>
                <option value="0-100">Under $100</option>
                <option value="100-200">$100 - $200</option>
                <option value="200-300">$200 - $300</option>
                <option value="300-1000">$300+</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
        {products.filter(filterProducts).map(product => (
          <div key={product.productID} className="col">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">{product.proFanciful}</h5>
                <h6 className="card-subtitle mb-2 text-muted">
                  {product.proVintage} {product.proVarietal}
                </h6>
                <p className="card-text">
                  <small className="text-muted">
                    {product.proAppellation} | {product.proWineType}
                  </small>
                </p>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="h5 mb-0">{product.wholesalePrice ? Number(product.wholesalePrice).toFixed(2) : "N/A"}</span>
                  <span className="badge bg-secondary">
                    Min. Order: {product.wholesaleMinQty}
                  </span>
                </div>
                <div className="input-group">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => handleQuantityChange(product.productID, (cart[product.productID] || 0) - 1)}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="form-control text-center"
                    value={cart[product.productID] || 0}
                    onChange={(e) => handleQuantityChange(product.productID, parseInt(e.target.value) || 0)}
                    min="0"
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => handleQuantityChange(product.productID, (cart[product.productID] || 0) + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {products.filter(filterProducts).length === 0 && (
        <div className="text-center py-5">
          <h4 className="text-muted">No products match your filters</h4>
          <button
            className="btn btn-outline-primary mt-3"
            onClick={() => setFilters({ varietal: '', vintage: '', priceRange: '' })}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Floating Cart Button for Mobile */}
      <div className="d-md-none position-fixed bottom-0 start-0 w-100 p-3 bg-white border-top">
        <button
          className="btn btn-primary w-100"
          onClick={handleCheckout}
          disabled={!Object.values(cart).some(quantity => quantity > 0)}
        >
          <ShoppingCart className="me-2" size={20} />
          Cart Total: ${getCartTotal().toFixed(2)}
        </button>
      </div>
    </div>
  );
};

export default WholesaleCatalog;